// ============================================================================
// MINISTRY ("TeamSuggestion") SELECTION — one team per question
// ============================================================================
//
// A class may ask a guest for more than one ministry: Life Church's Discover
// Life class asks for a Sunday team and a non-Sunday team (review call,
// 2026-09-03), and guests were picking the same team in both. This module is
// the single rule for keeping those picks distinct. The guest form uses
// `ministriesToExclude` to drop already-chosen teams from a picker; the save path
// uses `findDuplicateMinistrySelections` to reject what the form did not catch
// (a stale page, a hand-built request). Both read the same option flag, so
// admin intent and enforcement cannot drift apart.
//
// Values are the stored submission values for TeamSuggestion inputs: the
// team NAME the guest's dropdown holds (see the guest TeamSuggestion
// component — its option ids are team names), keyed by FormInput id.

export const TEAM_SUGGESTION_INPUT_TYPE = "TeamSuggestion";

/** The options JSON a TeamSuggestion FormInput carries, every key present. */
export interface TeamSuggestionOptions {
  /** Ministry ids this input never offers or lists. */
  hideTeams: string[];
  /** Show each ministry's description ("Our Ministries"). */
  showDescriptions: boolean;
  /** Offer the selection dropdown at all (false = display-only block). */
  showPicker: boolean;
  /** Show the "Suggested Ministries" list from the guest's test results. */
  showRecommend: boolean;
  /**
   * Take part in "one ministry per question": hide teams the guest already
   * chose in another participating question of the same class, and reject a
   * save that repeats one. The rule is SYMMETRIC: turning it off on either of
   * two questions frees that pair in both directions, so an admin who flips it
   * off on the question where it bit them gets what they asked for. Defaults
   * ON so every existing multi-question class gets the fix without an admin
   * visit.
   */
  excludeChosenElsewhere: boolean;
}

export const DEFAULT_TEAM_SUGGESTION_OPTIONS: Readonly<TeamSuggestionOptions> =
  Object.freeze({
    excludeChosenElsewhere: true,
    hideTeams: [],
    showDescriptions: true,
    showPicker: true,
    showRecommend: true,
  });

const boolOr = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

/**
 * The options of a TeamSuggestion input with every key filled in. Legacy rows
 * predate `showPicker` and `excludeChosenElsewhere`, and a few hold the
 * FormOption[] shape of other input types; anything unreadable falls back to
 * the default.
 */
export function normalizeTeamSuggestionOptions(
  options: unknown,
): TeamSuggestionOptions {
  const raw =
    options && typeof options === "object" && !Array.isArray(options)
      ? (options as Partial<Record<keyof TeamSuggestionOptions, unknown>>)
      : {};
  const d = DEFAULT_TEAM_SUGGESTION_OPTIONS;

  return {
    excludeChosenElsewhere: boolOr(
      raw.excludeChosenElsewhere,
      d.excludeChosenElsewhere,
    ),
    hideTeams: Array.isArray(raw.hideTeams)
      ? raw.hideTeams.filter((t): t is string => typeof t === "string")
      : [...d.hideTeams],
    showDescriptions: boolOr(raw.showDescriptions, d.showDescriptions),
    showPicker: boolOr(raw.showPicker, d.showPicker),
    showRecommend: boolOr(raw.showRecommend, d.showRecommend),
  };
}

/** The slice of a FormInput the exclusion rule reads. */
export interface MinistrySelectionInput {
  id: string;
  type?: string | null;
  options?: unknown;
}

export const isTeamSuggestionInput = (input: MinistrySelectionInput): boolean =>
  input.type === TEAM_SUGGESTION_INPUT_TYPE;

/** The team a stored value names, or null when blank / not a pick. */
export function selectedMinistry(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const team = value.trim();

  return team === "" ? null : team;
}

/** Submission values keyed by FormInput id (one section's, or a whole run's). */
export type MinistrySelectionValues = Record<string, unknown>;

/** A ministry input that takes part in the rule (type + flag on). */
export const participates = (input: MinistrySelectionInput): boolean =>
  isTeamSuggestionInput(input) &&
  normalizeTeamSuggestionOptions(input.options).excludeChosenElsewhere;

/** What both pick rules need: one question, the section's inputs and values. */
export interface PickRuleArgs {
  chosenElsewhere?: readonly string[];
  inputId: string;
  inputs: readonly MinistrySelectionInput[];
  values: MinistrySelectionValues;
}

/** The teams currently chosen in the given (already filtered) inputs. */
const picksOf = (
  inputs: readonly MinistrySelectionInput[],
  values: MinistrySelectionValues,
): string[] =>
  inputs
    .map((i) => selectedMinistry(values[i.id]))
    .filter((t): t is string => t !== null);

/** Ministry inputs before `inputId` in the given order that hold a team. */
const earlierPicks = (
  inputs: readonly MinistrySelectionInput[],
  inputId: string,
  values: MinistrySelectionValues,
): string[] => {
  const at = inputs.findIndex((i) => i.id === inputId);

  return picksOf(inputs.slice(0, at < 0 ? 0 : at).filter(participates), values);
};

/**
 * The teams `inputId`'s picker must not offer: every team chosen in another
 * PARTICIPATING ministry input of the same section (`values`), plus
 * `chosenElsewhere` (the run's picks in other sections, which the page loads
 * separately and which the server already limits to participating inputs).
 * Empty when the input itself opts out, is not a ministry input, or is
 * unknown.
 */
export function ministriesToExclude({
  chosenElsewhere = [],
  inputId,
  inputs,
  values,
}: PickRuleArgs): string[] {
  const self = inputs.find((i) => i.id === inputId);

  if (!self || !participates(self)) return [];

  const siblings = picksOf(
    inputs.filter((i) => i.id !== inputId && participates(i)),
    values,
  );

  return [...new Set([...siblings, ...chosenElsewhere])];
}

/**
 * Should `inputId` give up its current pick? Only when an EARLIER question
 * (by the given order) or another section already holds it: the later
 * question yields, the earlier one keeps its answer, and the guest sees one
 * filled question plus one empty one with the note explaining why. (Clearing
 * both would leave two blanks and, because nothing is excluded any more, no
 * note at all.)
 */
export function shouldYieldPick({
  chosenElsewhere = [],
  inputId,
  inputs,
  values,
}: PickRuleArgs): boolean {
  const self = inputs.find((i) => i.id === inputId);
  const own = selectedMinistry(values[inputId]);

  if (own === null || !self || !participates(self)) return false;

  return (
    chosenElsewhere.includes(own) ||
    earlierPicks(inputs, inputId, values).includes(own)
  );
}

export interface DuplicateMinistrySelection {
  /** The ids of the inputs that all hold `team`. */
  inputIds: string[];
  team: string;
}

/**
 * Teams chosen by two or more PARTICIPATING ministry inputs. An input that
 * opted out never counts, in either direction: a church that wants "first
 * choice / second choice" answered the same way turns the rule off on one of
 * them and the pair is free, matching what the picker offers.
 *
 * `involving` narrows the result to duplicates that include at least one of
 * those input ids: the save path passes the section being saved, so a stale
 * duplicate between two OTHER sections cannot reject an unrelated save (a
 * Text or Completion section's empty mount-time save, for instance).
 */
export function findDuplicateMinistrySelections({
  inputs,
  involving,
  values,
}: {
  inputs: readonly MinistrySelectionInput[];
  involving?: ReadonlySet<string>;
  values: MinistrySelectionValues;
}): DuplicateMinistrySelection[] {
  const byTeam = new Map<string, MinistrySelectionInput[]>();

  for (const input of inputs) {
    if (!participates(input)) continue;
    const team = selectedMinistry(values[input.id]);

    if (team === null) continue;
    byTeam.set(team, [...(byTeam.get(team) ?? []), input]);
  }

  return [...byTeam.entries()]
    .filter(
      ([, holders]) =>
        holders.length > 1 &&
        (!involving || holders.some((i) => involving.has(i.id))),
    )
    .map(([team, holders]) => ({ inputIds: holders.map((i) => i.id), team }));
}

/** The guest-facing message for a rejected duplicate (one line per team). */
export function duplicateMinistrySelectionMessage(
  duplicates: readonly DuplicateMinistrySelection[],
): string {
  const teams = duplicates.map((d) => d.team);
  const list =
    teams.length === 1
      ? teams[0]
      : `${teams.slice(0, -1).join(", ")} and ${teams[teams.length - 1]}`;

  return `You chose ${list} more than once. Please pick a different ministry for each question.`;
}
